import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    errorRedirect,
    FormStore,
    inject,
    Input,
    Loader,
    Nullable,
    SubTopBar,
    TasksStore,
    Title,
    upperCaseEachWord,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowLeftShort } from '@react-icons/all-files/bs/BsArrowLeftShort';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { Button, Form, FormGroup } from 'reactstrap';
import { PartDefinitionsStore } from './PartDefinitionsStore';

export interface IEditPartParams {
    partNumber: string;
}

export interface ICreateRevisionLocationState {
    copy?: string;
}

export interface ICreateRevisionProps extends RouteComponentProps<IEditPartParams, {}, ICreateRevisionLocationState> {}

@autoinject
@observer
export class CreateRevision extends React.Component<ICreateRevisionProps> {
    public constructor(props: ICreateRevisionProps) {
        super(props);
    }

    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            this.fillForm();
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div className="container-fluid pt-3">
                <Title>{this.viewState == 'edit' ? 'Edit' : 'Create'} Part Revision</Title>

                <SubTopBar>
                    <LinkContainer exact to="/part-definitions">
                        <a className="btn btn-link">
                            <BsArrowLeftShort style={{ fontSize: 25 }} /> Back To Part Definitions
                        </a>
                    </LinkContainer>
                </SubTopBar>

                <Loader
                    loading={this.loading && !this.partBaseForm}
                    message=" Loading Part Definition..."
                    showSpinner
                    className="w-100 text-center"
                />

                {this.partBaseForm && (
                    <Form onSubmit={this.submitAsync}>
                        <div className="row justify-content-center">
                            <div className="col-12 col-md-6 col-lg-5 mb-3">
                                <div className="card">
                                    <div className="card-header bg-btn-primary">Part Defintion Revision</div>
                                    <div className="card-body">
                                        {!!this.partBaseForm.globalErrors.length && (
                                            <div className="alert-danger p-2">
                                                <ul>
                                                    {this.partBaseForm.globalErrors.map((e: string, idx: number) => (
                                                        <li key={idx}>{e}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        <div>
                                            <FormGroup color={this.partBaseForm.fields.revision.state}>
                                                <label>
                                                    <strong>Revision</strong>
                                                </label>

                                                <Input
                                                    type="text"
                                                    field={this.partBaseForm.fields.revision}
                                                    placeholder="Revision"
                                                    required
                                                />
                                            </FormGroup>

                                            <FormGroup color={this.partBaseForm.fields.comments.state}>
                                                <label>
                                                    <strong>Comments</strong>
                                                </label>

                                                <Input
                                                    type="textarea"
                                                    field={this.partBaseForm.fields.comments}
                                                    placeholder="Comments"
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <Button type="submit" color="primary">
                                                    <span>
                                                        {this.viewState == 'create' && 'Create New Revision'}
                                                        {this.viewState == 'edit' && 'Update Revision'}
                                                    </span>
                                                </Button>
                                            </FormGroup>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </div>
        );
    }

    @computed
    private get viewState() {
        const parthParts = this.props.location.pathname.split('/');
        return parthParts[parthParts.length - 1];
    }

    @computed
    private get part() {
        return this.partDefsStore.partDefinitionBases.get(
            this.props.match.params.partNumber ?? this.props.location.state?.copy
        );
    }

    @action
    private fillForm() {
        if (this.viewState == 'edit' && !this.part) throw new Error('Part does not exist');

        if (!this.part) {
            const model: Nullable<Mri.Parts.PartDefinition> = {
                partNumber: this.props.match.params.partNumber,
                revision: '',
                comments: '',
                isTopLevel: false,
                isAssembly: false,
                isAssemblyItem: false,
                partCount: 0,
                partDefinitionGuid: uuidv4generate(),
            };

            this.partBaseForm = new FormStore<Mri.Parts.PartDefinition>(model);
        }
    }

    @bind
    @action
    private async submitAsync(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!this.partBaseForm) throw new Error('Form does not exist');

        this.partBaseForm.onSubmit();
        if (this.partBaseForm.validate()) {
            this.loading = true;

            const task = this.tasksStore.startSessionTask(
                upperCaseEachWord(this.viewState) + ' Part Definition Revision'
            );

            try {
                if (this.viewState == 'create') {
                    await this.partDefsStore.createRevisionAsync(this.partBaseForm.values);
                }
                task.complete();
                this.props.history.push('/part-definitions');
            } catch (e: any) {
                if (e.modelState) {
                    this.partBaseForm.clearGlobalErrors();
                    this.partBaseForm.addErrors(e.modelState);
                }

                task.error(e);
            } finally {
                this.loading = false;
            }
        } else {
            console.log('invalid');
            this.loading = false;
        }
    }

    @observable private partBaseForm?: FormStore<Mri.Parts.PartDefinition>;
    @observable private loading = false;

    @inject private readonly partDefsStore!: PartDefinitionsStore;
    @inject private tasksStore!: TasksStore;
}
